// Are a flat quad's UVs the right way round?
//
// In range is not the same as correct, and that difference has now cost
// something twice.
//
// When the billboards were first textured, their UVs were out of range
// (u from -2.4 to 4.0) and a range check in AdBoards.jsx caught it and
// projected new ones. The models have been re-exported since. On 25 Sep
// billboard_salon_screen reads:
//
//   corners  (-1.19, 18.77, -5.96) (-1.19, 12.13, -5.96)
//            (-1.19, 18.77,  5.96) (-1.19, 12.13,  5.96)
//   uvs      (0, 1)  (0.557, 1)  (0, 0)  (0.557, 0)
//
// which is in range, spans more than half in both directions, and passes
// every check there was. It is also mapped with u running down the HEIGHT
// of the board and v across its WIDTH, and u stopping at 0.557. So his
// shader went up on the town's billboard rotated a quarter turn and
// squashed into 56% of one axis. Sam: "it's not centered properly given
// the aspect of the billboard."
//
// Numbers cannot say whether they are the right way round; only the
// geometry can. So this asks the quad: does u track your right axis and v
// your down axis. Correlation rather than equality, because a
// legitimately inset or trimmed unwrap is still the right way round.
//
// Plain arrays, no three import, because the whole point is that it can
// be run against the real corner numbers in a test.

/** Pearson correlation. Zero when either side does not vary at all. */
export function corr(a, b) {
  const mean = (xs) => xs.reduce((n, x) => n + x, 0) / xs.length
  const ma = mean(a)
  const mb = mean(b)
  let top = 0, sa = 0, sb = 0
  for (let i = 0; i < a.length; i++) {
    const da = a[i] - ma
    const db = b[i] - mb
    top += da * db
    sa += da * da
    sb += db * db
  }
  if (sa < 1e-9 || sb < 1e-9) return 0
  return top / Math.sqrt(sa * sb)
}

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
]
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
function unit(v) {
  const n = Math.hypot(v[0], v[1], v[2])
  return n < 1e-9 ? null : [v[0] / n, v[1] / n, v[2] / n]
}

const UP = [0, 1, 0]

/**
 * The quad's own axes: right across it, down it. Taken from the face
 * normal, which is what "the way round the quad hangs" means for a
 * surface somebody stood a texture up on.
 *
 * A quad lying flat has no up in its own plane. None of ours do, and a
 * degenerate cross product would produce NaN, so it falls back to +x.
 */
export function quadAxes(normal) {
  const n = unit(normal) || [0, 0, 1]
  const right = unit(cross(UP, n)) || [1, 0, 0]
  // Down, not up: with a viewer-facing normal this points at the floor,
  // which is where v=0 belongs. Every texture here loads flipY false, the
  // glTF convention, so v runs downwards.
  const down = unit(cross(right, n)) || [0, -1, 0]
  return { right, down }
}

/**
 * @param corners four world-space [x,y,z], in the geometry's own order
 * @param uvs     four [u,v], same order
 * @param normal  the face normal, in world space
 * @returns true when u tracks the quad's right axis and v its down axis
 */
export function uvsMatchAxes(corners, uvs, normal, floor = 0.9) {
  if (!Array.isArray(corners) || corners.length !== 4) return true
  if (!Array.isArray(uvs) || uvs.length !== 4) return true
  const { right, down } = quadAxes(normal)
  const rs = corners.map((p) => dot(p, right))
  const ds = corners.map((p) => dot(p, down))
  const us = uvs.map((t) => t[0])
  const vs = uvs.map((t) => t[1])
  return corr(rs, us) > floor && corr(ds, vs) > floor
}

/**
 * Planar UVs for a flat quad, projected from its own corners. Not an
 * approximation: for four coplanar corners this is exactly what a correct
 * unwrap produces.
 *
 * @returns eight numbers, u then v per corner, or null if the quad is
 *          degenerate in either direction
 */
export function planarUVsFor(corners, normal) {
  if (!Array.isArray(corners) || corners.length !== 4) return null
  const { right, down } = quadAxes(normal)
  const rs = corners.map((p) => dot(p, right))
  const ds = corners.map((p) => dot(p, down))
  const rMin = Math.min(...rs), rSpan = Math.max(...rs) - rMin
  const dMin = Math.min(...ds), dSpan = Math.max(...ds) - dMin
  if (rSpan < 1e-6 || dSpan < 1e-6) return null
  const out = new Float32Array(8)
  for (let i = 0; i < 4; i++) {
    out[i * 2] = (rs[i] - rMin) / rSpan
    out[i * 2 + 1] = (ds[i] - dMin) / dSpan
  }
  return out
}

export const _axes = { sub, cross, dot, unit, UP }
